import {
  Box,
  Divider,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import ImageSlider from "../ImageSlider/ImageSlider";
import { useAppDispatch, useAppSelector } from "shared/lib/hooks/hooks";
import { IProducts } from "pages/RegistrationPage/RegistrationPage";
import { setProductQuantity, setProducts } from "api/slice/productsSlice/productsSlice";


const Products: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const productsResponse = useAppSelector(
    (state) => state.products.productsById.response
  );

  const products = useAppSelector(
    (state) => state.products.productsById.params.products
  );

  const quantities = useAppSelector(
    (state) => state.products.productsById.params.quantities || {}
  );

  const selectedProducts: IProducts[] = products ?? [];
  const defaultImage = process.env.REACT_APP_DEFAULT_IMAGE_URL;

  const setProductsState = (
    id: number,
    quantity: number,
    price: number,
    name: string
  ) => {
    let nextProducts = [...selectedProducts];

    if (quantity === 0) {
      nextProducts = nextProducts.filter((p) => p.productId !== id);
    } else {
      const existing = nextProducts.find((p) => p.productId === id);
      if (existing) {
        nextProducts = nextProducts.map((p) =>
          p.productId === id ? { ...p, quantity } : p
        );
      } else {
        nextProducts.push({ productId: id, quantity, price, name });
      }
    }

    dispatch(setProducts(nextProducts));
  };

  const handleIncrease = (
    id: number,
    max: number,
    price: number,
    name: string
  ) => {
    const next = Math.min((quantities[id] || 0) + 1, max);
    dispatch(setProductQuantity({ ...quantities, [id]: next }));
    setProductsState(id, next, price, name);
  };

  const handleDecrease = (id: number, price: number, name: string) => {
    const next = Math.max((quantities[id] || 0) - 1, 0);
    dispatch(setProductQuantity({ ...quantities, [id]: next }));
    setProductsState(id, next, price, name);
  };

  return (
    <Paper sx={{ borderRadius: "16px", p: 3, boxShadow: "none" }}>
      <Box display="flex" columnGap={2} pb={2}>
        <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
          {t("registerToMarathon.products")}
        </Typography>
        <Divider
          sx={{
            flexGrow: 1,
            height: "1px",
            my: "auto",
            backgroundColor: "#ECF2F7",
          }}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          width: "100%",
          alignItems: "stretch",
          gridTemplateColumns: "repeat(3, 1fr)",

          "@media (max-width:1500px)": {
            gridTemplateColumns: "repeat(2, 1fr)",
          },

          "@media (max-width:1300px)": {
            gridTemplateColumns: "repeat(2, 1fr)",
          },

          "@media (max-width:1200px)": {
            gridTemplateColumns: "repeat(1, 1fr)",
          },

          "@media (max-width:900px)": {
            gridTemplateColumns: "repeat(2, 1fr)",
          },

          "@media (max-width:600px)": {
            gridTemplateColumns: "1fr",
          },
          "& > *": { display: "flex", flexDirection: "column" },
        }}
      >
        {productsResponse?.data?.length === 0 ? (
          <Box
            sx={{
              gridColumn: "1 / -1",
              textAlign: "center",
              py: 6,
              color: "#68717B",
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
              {t("registerToMarathon.noProducts")}
            </Typography>
          </Box>
        ) : (
          productsResponse?.data?.map((item) => {
            const quantity = quantities[item.id] || 0;

            return (
              <Card
                key={item.id}
                sx={{
                  borderRadius: "16px",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <ImageSlider
                  imagePaths={
                    item.imagePaths
                      ?.map(
                        (path) => path && `${defaultImage}${path}`
                      )
                      .filter(Boolean) || [defaultImage]
                  }
                />

                <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    pb={1}
                    sx={{ flexGrow: 1 }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 16, md: 18 },
                        fontWeight: 600,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.name}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: { xs: 20, sm: 18, md: 20, xl: 25 },
                        fontWeight: 600,
                      }}
                    >
                      {item.price} {t("myMarathon.som")}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#0090D1",
                      pb: 1,
                    }}
                  >
                    {t("registerToMarathon.left")} {item.available}{" "}
                    {t("registerToMarathon.pieces")}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#68717B",
                      pb: 2,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {item.description}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      background: "#0090D1",
                      borderRadius: "12px",
                      p: 1,
                      mt: "auto",
                    }}
                  >
                    {quantity > 0 && (
                      <Button
                        onClick={() =>
                          handleDecrease(item.id, item.price, item.name)
                        }
                        variant="contained"
                        sx={{
                          minWidth: 36,
                          background: "white",
                          color: "#0090D1",
                        }}
                      >
                        -
                      </Button>
                    )}

                    <Button
                      startIcon={!quantity && <ShoppingBagIcon />}
                      onClick={() =>
                        handleIncrease(
                          item.id,
                          item.available,
                          item.price,
                          item.name
                        )
                      }
                      sx={{
                        flexGrow: 1,
                        color: "white",
                        borderRadius: "12px",
                      }}
                    >
                      {quantity ? quantity : t("buttons.add")}
                    </Button>

                    {quantity > 0 && (
                      <Button
                        onClick={() =>
                          handleIncrease(
                            item.id,
                            item.available,
                            item.price,
                            item.name
                          )
                        }
                        disabled={quantity >= item.available}
                        variant="contained"
                        sx={{
                          minWidth: 36,
                          background: "white",
                          color: "#0090D1",
                        }}
                      >
                        +
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>
    </Paper>
  );
};

export default Products;
